import { useBlocker } from '@tanstack/react-router';
import { useState } from 'react';

/**
 * Result from the unsaved navigation guard.
 */
export interface UnsavedNavigationGuardResult {
  /** Whether navigation is currently blocked due to unsaved changes. */
  isBlocked: boolean;
  /** Proceed with the pending navigation. */
  proceed: () => void;
  /** Cancel navigation and stay on the current page. */
  stay: () => void;
}

/**
 * Wraps TanStack Router's useBlocker to guard SPA navigation and beforeunload
 * when there are unsaved changes.
 *
 * - Blocks router navigation with a resolver when `isDirty` is true
 * - Enables browser beforeunload prompt only when `isDirty` is true
 * - Returns helpers to proceed/reset the pending navigation
 */
export const useUnsavedNavigationGuard = (
  isDirty: boolean
): UnsavedNavigationGuardResult => {
  const resolver = useBlocker({
    shouldBlockFn: () => isDirty,
    enableBeforeUnload: () => isDirty,
    withResolver: true,
  });

  return {
    isBlocked: resolver.status === 'blocked',
    proceed: () => resolver.proceed?.(),
    stay: () => resolver.reset?.(),
  };
};
/**
 * Result from the unsaved navigation guard with built-in state management.
 * Extends the base guard result to include state for tracking unsaved changes.
 *
 * @see useUnsavedNavigationGuardState - Hook that implements this interface
 */
export interface UnsavedNavigationGuardStateResult
  extends UnsavedNavigationGuardResult {
  /** Whether there are currently unsaved changes. */
  hasUnsavedChanges: boolean;
  /** Updates the unsaved changes state. */
  setHasUnsavedChanges: (next: boolean) => void;
}

/**
 * Options for configuring the unsaved navigation guard with state.
 */
export const useUnsavedNavigationGuardState = (
  initial: boolean = false
): UnsavedNavigationGuardStateResult => {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(initial);
  const guard = useUnsavedNavigationGuard(hasUnsavedChanges);

  return {
    ...guard,
    hasUnsavedChanges,
    setHasUnsavedChanges,
  };
};
